import React, { useCallback, useEffect, useState } from "react";
import { ObjectInspectSidebar } from "./ObjectInspectSidebar";
import { useSidebarContext } from "../SidebarContext";
import { useObjectSettings } from "../contexts/ObjectSettingsProvider";
import notificationsCenter from "../../singleton/NotificationsCenter";
import bwipjs from "@bwip-js/browser";

import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  Button,
  makeStyles,
  Text,
  Caption1,
  Divider,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel
} from "@fluentui/react-components";
import { Dismiss24Regular, CopyRegular, ArrowDownloadFilled } from "@fluentui/react-icons";
import configs from "../../utils/configs";
import { CAMERA_MODE_INSPECT } from "../../systems/camera-system";
import { useObjectList } from "./useObjectList";

const useStyles = makeStyles({
  drawer: {
    width: "463px"
  },
  headerDrawer: {
    display: " flex",
    padding: " var(--Font-size-600, 24px) 16px 12px var(--Font-size-600, 24px)",
    alignItems: " flex-start",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: "#16181E"
  },
  drawerBody: {
    paddingTop: "20px"
  },
  search: {
    backgroundColor: "rgba(255, 255, 255, 0.10)",
    width: "100%"
  },
  section: {
    display: " flex",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "2px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--Gray-3, #828282)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "32px",
    "& .fui-Avatar__image": {
      width: "32px",
      height: "32px"
    },
    "& .fui-Avatar": {
      height: "32px"
    },
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "32px"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px"
  },
  footerParticipants: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: "#16181E"
  },
  redIcon: {
    color: "var(--Red, #EB5757)"
  },
  avatarGroupItem: {
    backgroundColor: "#333",
    "& img": {
      backgroundColor: "#333"
    }
  },
  avatarGroupItemAll: {
    "& img": {
      backgroundColor: "#333"
    }
  },
  qrCodeContainer: {
    display: " flex",
    alignItems: " flex-end",
    gap: " 6px"
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "var(--Font-size-100, 10px)"
  },
  divider: {
    padding: "5px 0px 4px 0px",
    marginTop: "20px",
    marginBottom: "20px"
  },
  accordionHeaderItem: {
    "& button": {
      paddingLeft: "0px"
    }
  }
});

export function ArtworkInfoSidebarContainer({ hubChannel, scene }) {
  const styles = useStyles();
  const [arUrl, setARUrl] = useState(null);
  const [artworkInfo, setArtworkInfo] = useState(null);

  const { setSidebar } = useSidebarContext();

  const { selectedObject } = useObjectList();
  const qrCanvasRef = React.useRef(null); // Ref to the QR code canvas

  const [isOpen, setIsOpen] = React.useState(true);
  const artworkQREl = selectedObject?.el;

  const metadata = artworkInfo?.metadata;
  useEffect(() => {

  }, [metadata])

  const copyUrl = url => {
    navigator.clipboard
      .writeText(url)
      .then(() => {})
      .catch(err => {
        console.error("Failed to copy: ", err);
      });
  };

  const copyQRCode = () => {
    copyUrl(arUrl);
    notificationsCenter.success("QR Code has been copied");
  };

  const downloadQRCode = () => {
    const canvas = qrCanvasRef.current;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = "qrcode.png";
    link.click();
  };

  const generateQRCode = url => {
    bwipjs.toCanvas(qrCanvasRef.current, {
      bcid: "qrcode",
      text: url,
      scaleX: 2,
      scaleY: 2,
      includetext: false,
      textxalign: "center",
      paddingwidth: 3,
      paddingheight: 3,
      backgroundcolor: "#fff"
    });
    // setQRCanvas(canvas);
  };

  useEffect(() => {
    const qrCodeId = artworkQREl?.components["artwork"]?.data.qrCodeId;
    const arUrl = qrCodeId ? `${configs["AR_EDITOR"]}/view/${qrCodeId}` : null;
    if (arUrl) {
      setARUrl(arUrl);
    }
  }, [selectedObject, artworkQREl]);

  useEffect(() => {
    const artworkData = selectedObject?.el?.components["artwork"]?.data?.artworkInfo;
    if (artworkData) {
      setArtworkInfo(artworkData);
    }
  }, [selectedObject]);

  useEffect(() => {
    if (arUrl) {
      generateQRCode(arUrl);
    }
  }, [arUrl]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    setSidebar(null);
    window.APP.scene.systems["hubs-systems"].cameraSystem.uninspect();
  }, []);

  const cameraMode = window.APP.scene.systems["hubs-systems"].cameraSystem.mode;

  React.useEffect(() => {
    if (cameraMode != CAMERA_MODE_INSPECT) {
      onClose();
    }
  }, [cameraMode]);

  return (
    <OverlayDrawer modalType="non-modal" position="end" as="aside" open={isOpen} className={styles.drawer}>
      <DrawerHeader className={styles.headerDrawer}>
        <DrawerHeaderTitle
          style={{ width: "100%" }}
          action={<Button onClick={onClose} appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />}
        >
          Art Label
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <Text size={"small"}>
              <Caption1 style={{ color: "#828282" }}>Scan to view in AR</Caption1>
            </Text>

            <div className={styles.qrCodeContainer}>
              <canvas
                id="qr-info"
                ref={qrCanvasRef}
                style={{ borderRadius: "4px", border: "1px solid #BDBDBD" }}
              ></canvas>
              <div className={styles.buttonsContainer}>
                <Button appearance="outline" icon={<CopyRegular />} onClick={copyQRCode} />
                <Button appearance="primary" icon={<ArrowDownloadFilled />} onClick={downloadQRCode} />
              </div>
            </div>
          </div>
          <Text style={{ fontSize: "18px" }} weight="semibold">
            {artworkInfo?.author}
          </Text>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Text size={400} weight="semibold">
              {artworkInfo?.title}
              {artworkInfo?.artworkDate && <>, {artworkInfo?.artworkDate}</>}
            </Text>
            <Text size={400}>{artworkInfo?.medium == "Custom" ? artworkInfo?.metadata?.customMedium : artworkInfo?.medium }</Text>
            {artworkInfo?.dimensions && <Text size={400}>{artworkInfo?.dimensions}</Text>}
          </div>
          <Text size={400}>{artworkInfo?.description}</Text>
        </div>
        {(metadata?.techniqueUsed || metadata?.currentPhysicalWork 
          || metadata?.artistBio || artworkInfo?.artistStatement || metadata?.aiToolUsed || metadata?.workCreatedByAI) && (
          <>
        <Divider className={styles.divider} />
        <Accordion collapsible multiple>
          <AccordionItem value="1">
            <AccordionHeader className={styles.accordionHeaderItem}>
              <Text size={200} style={{ lineHeight: "20px" }}>
                More Info
              </Text>
            </AccordionHeader>
            <AccordionPanel style={{ display: "flex", flexDirection: "column", marginLeft: "4px" }}>
              {(metadata?.techniqueUsed || metadata?.currentPhysicalWork) && (
                <>
                  <Text size={400} weight="semibold">
                    Work Information
                  </Text>
                  <Accordion multiple openItems={["2", "3"]} defaultValue={["2", "3"]}>
                    {metadata?.techniqueUsed && (
                      <AccordionItem value="2">
                        <AccordionHeader
                          style={{ marginLeft: "-7px" }}
                          className={styles.accordionHeaderItem}
                          expandIcon={<></>}
                        >
                          <Text size={200} style={{ lineHeight: "20px" }}>
                            Digitization technique used
                          </Text>
                        </AccordionHeader>
                        <AccordionPanel
                          style={{ display: "flex", flexDirection: "column", gap: "20px", marginLeft: "4px" }}
                        >
                          <Text size={300} weight="semibold">
                            {metadata?.techniqueUsed}
                          </Text>
                        </AccordionPanel>
                      </AccordionItem>
                    )}

                    {metadata?.currentPhysicalWork && (
                      <AccordionItem value="3">
                        <AccordionHeader
                          style={{ marginLeft: "-7px" }}
                          className={styles.accordionHeaderItem}
                          expandIcon={<></>}
                        >
                          <Text size={200} style={{ lineHeight: "20px" }}>
                            Current State of Physical Works
                          </Text>
                        </AccordionHeader>
                        <AccordionPanel
                          style={{ display: "flex", flexDirection: "column", gap: "20px", marginLeft: "4px" }}
                        >
                          <Text size={300} weight="semibold">
                            {metadata?.currentPhysicalWork}
                          </Text>
                        </AccordionPanel>
                      </AccordionItem>
                    )}
                  </Accordion>
                  <Divider className={styles.divider} />
                </>
              )}
              {(metadata?.artistBio || artworkInfo?.artistStatement) && (
                <>
                  <Text size={400} weight="semibold">
                    Artist Information
                  </Text>
                  <Accordion multiple openItems={["2", "3"]} defaultValue={["2", "3"]}>
                    {metadata?.artistBio && (
                      <AccordionItem value="2">
                        <AccordionHeader
                          style={{ marginLeft: "-7px" }}
                          className={styles.accordionHeaderItem}
                          expandIcon={<></>}
                        >
                          <Text size={200} style={{ lineHeight: "20px" }}>
                            Bio
                          </Text>
                        </AccordionHeader>
                        <AccordionPanel
                          style={{ display: "flex", flexDirection: "column", gap: "20px", marginLeft: "4px" }}
                        >
                          <Text size={300}>{metadata?.artistBio}</Text>
                        </AccordionPanel>
                      </AccordionItem>
                    )}
                    {artworkInfo?.artistStatement && (
                      <AccordionItem value="3">
                        <AccordionHeader
                          style={{ marginLeft: "-7px" }}
                          className={styles.accordionHeaderItem}
                          expandIcon={<></>}
                        >
                          <Text size={200} style={{ lineHeight: "20px" }}>
                            Statement
                          </Text>
                        </AccordionHeader>
                        <AccordionPanel
                          style={{ display: "flex", flexDirection: "column", gap: "20px", marginLeft: "4px" }}
                        >
                          <Text size={300}>{artworkInfo?.artistStatement}</Text>
                        </AccordionPanel>
                      </AccordionItem>
                    )}
                  </Accordion>
                  <Divider className={styles.divider} />
                </>
              )}
              {(metadata?.workCreatedByAI && metadata?.aiToolUsed) && (
              <>
                <Text size={400} weight="semibold">
                  AI-generated Artwork
                </Text>
                <Accordion multiple openItems={["2", "3"]} defaultValue={["2", "3"]}>
                  {metadata?.aiToolUsed && (
                    <AccordionItem value="3">
                      <AccordionHeader
                        style={{ marginLeft: "-7px" }}
                        className={styles.accordionHeaderItem}
                        expandIcon={<></>}
                      >
                        <Text size={200} style={{ lineHeight: "20px" }}>
                          AI Tool Used
                        </Text>
                      </AccordionHeader>
                      <AccordionPanel
                        style={{ display: "flex", flexDirection: "column", gap: "20px", marginLeft: "4px" }}
                      >
                        <Text size={300}>{metadata?.aiToolUsed}</Text>
                      </AccordionPanel>
                    </AccordionItem>
                  )}
                </Accordion>
              </>
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
          </>
        )}
      </DrawerBody>
    </OverlayDrawer>
  );
}
